import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { resetPassword } from "../api/resetPassword";

interface FormData {
  new_password: string;
  new_password_confirmation: string;
}

function ResetPassword(): React.JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    new_password: "",
    new_password_confirmation: "",
  });

  const [passwordError, setPasswordError] = useState<string>("");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to parse query params from URL
  const useQuery = () => {
    return new URLSearchParams(location.search);
  };

  const query = useQuery();
  const uidb64 = query.get("uid");
  const token = query.get("token");

  useEffect(() => {
    if (!uidb64 || !token) {
      navigate("/");
    }
  }, [uidb64, token, navigate]);

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Check if new passwords match
    if (formData.new_password !== formData.new_password_confirmation) {
      setPasswordError("Passwords do not match.");
      return;
    }

    // Check if password is at least 8 characters long
    if (formData.new_password.length < 8) {
      setPasswordError("Password should be at least 8 characters long.");
      return;
    }

    try {
      await resetPassword(
        {
          new_password: formData.new_password,
        },
        uidb64 || "",
        token || "",
      );
      setIsSuccess(true);
      setPasswordError("Password has been reset. Redirecting to login...");
    } catch (error) {
      setPasswordError("Password reset failed. Please try again.");
      console.error("Error during password reset:", error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <div className="w-full max-w-[400px]">
        <form
          className="flex w-full flex-col items-center justify-center rounded-lg bg-white p-8 shadow-lg"
          onSubmit={handleSubmit}
        >
          <h2 className="mb-4 text-3xl font-bold text-blue">Reset Password</h2>
          <div className="w-full space-y-4">
            <input
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              placeholder="New Password"
              className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-blue"
            />
            <input
              type="password"
              name="new_password_confirmation"
              value={formData.new_password_confirmation}
              onChange={handleChange}
              placeholder="Confirm New Password"
              className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-blue"
            />
            {passwordError && (
              <div
                className={`text-center ${isSuccess ? "text-blue" : "text-red-500"}`}
              >
                {passwordError}
              </div>
            )}
            <button
              type="submit"
              className="w-full rounded-lg bg-blue px-4 py-2 text-white transition-colors hover:bg-blue-dark"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
